/**
 * Solární fáze dne — elevace slunce (NOAA-ish, bez externích závislostí).
 *
 * Elevace (°) rozhoduje o fázi; ráno/večer rozliší `isMorning` (hodinový úhel).
 * Default souřadnice = Praha (MaxCloud CZ), přepsatelné geolokací.
 */
import i18n from './i18n';

export type SolarPhase =
  | 'midnight'
  | 'night'
  | 'nautical_dawn'
  | 'civil_dawn'
  | 'sunrise'
  | 'morning'
  | 'noon'
  | 'afternoon'
  | 'golden'
  | 'sunset'
  | 'civil_dusk'
  | 'nautical_dusk';

export interface SolarSnapshot {
  phase: SolarPhase;
  /** Úhel slunce nad horizontem (°) — záporný = pod obzorem */
  elevation: number;
  /** true = slunce ještě stoupá (dopoledne) */
  rising: boolean;
  lat: number;
  lon: number;
  at: Date;
}

/** Praha — fallback bez geolokace. */
export const DEFAULT_SOLAR_COORDS = { lat: 50.0755, lon: 14.4378 } as const;

const PHASE_LABEL_CS: Record<SolarPhase, string> = {
  midnight: 'Půlnoc',
  night: 'Noc',
  nautical_dawn: 'Nautické svítání',
  civil_dawn: 'Civilní svítání',
  sunrise: 'Východ slunce',
  morning: 'Dopoledne',
  noon: 'Poledne',
  afternoon: 'Odpoledne',
  golden: 'Zlatá hodina',
  sunset: 'Západ slunce',
  civil_dusk: 'Civilní soumrak',
  nautical_dusk: 'Nautický soumrak',
};

export function solarPhaseLabel(phase: SolarPhase, _lang = 'cs'): string {
  return i18n.t(`layout.theme.phase.${phase}`, PHASE_LABEL_CS[phase]);
}

function toDeg(r: number): number {
  return (r * 180) / Math.PI;
}

/**
 * Elevace + azimut slunce (°) pro daný okamžik a souřadnice.
 * Algoritmus dle NOAA Solar Calculator (zjednodušený, ±0.5° stačí pro UI fáze).
 */
export function solarPosition(
  date: Date,
  lat: number,
  lon: number,
): { elevation: number; azimuth: number; rising: boolean } {
  const rad = Math.PI / 180;
  const dayMs = 1000 * 60 * 60 * 24;
  const j1970 = 2440588;
  const j2000 = 2451545;

  const d = date.getTime() / dayMs - 0.5 + j1970 - j2000;

  // Mean anomaly, center, ecliptic longitude
  const M = rad * (357.5291 + 0.98560028 * d);
  const C = rad * (1.9148 * Math.sin(M) + 0.02 * Math.sin(2 * M) + 0.0003 * Math.sin(3 * M));
  const P = rad * 102.9372;
  const L = M + C + P + Math.PI;

  const sinDec = Math.sin(L) * Math.sin(rad * 23.4397);
  const cosDec = Math.cos(Math.asin(sinDec));

  // Sidereal time
  const lw = rad * -lon;
  const phi = rad * lat;
  const H = rad * (280.16 + 360.9856235 * d) - lw - L;

  const azimuth = Math.atan2(
    Math.sin(H),
    Math.cos(H) * Math.sin(phi) - (sinDec / cosDec) * Math.cos(phi),
  );
  const elevation = Math.asin(
    Math.sin(phi) * sinDec + Math.cos(phi) * cosDec * Math.cos(H),
  );

  // rising ≈ hour angle before solar noon (H negative in morning in this convention
  // for typical mid-latitudes when sun is climbing: cos(H) decreases after noon…)
  // Use d(elevation)/dt via finite difference instead — robust.
  const later = new Date(date.getTime() + 5 * 60 * 1000);
  const elevLater = solarElevationOnly(later, lat, lon);
  const elevNow = toDeg(elevation);
  const rising = elevLater >= elevNow - 0.01;

  return {
    elevation: elevNow,
    azimuth: (toDeg(azimuth) + 180) % 360,
    rising,
  };
}

function solarElevationOnly(date: Date, lat: number, lon: number): number {
  const rad = Math.PI / 180;
  const dayMs = 1000 * 60 * 60 * 24;
  const j1970 = 2440588;
  const j2000 = 2451545;
  const d = date.getTime() / dayMs - 0.5 + j1970 - j2000;
  const M = rad * (357.5291 + 0.98560028 * d);
  const C = rad * (1.9148 * Math.sin(M) + 0.02 * Math.sin(2 * M) + 0.0003 * Math.sin(3 * M));
  const P = rad * 102.9372;
  const L = M + C + P + Math.PI;
  const sinDec = Math.sin(L) * Math.sin(rad * 23.4397);
  const cosDec = Math.cos(Math.asin(sinDec));
  const lw = rad * -lon;
  const phi = rad * lat;
  const H = rad * (280.16 + 360.9856235 * d) - lw - L;
  return toDeg(Math.asin(Math.sin(phi) * sinDec + Math.cos(phi) * cosDec * Math.cos(H)));
}

/**
 * Mapování elevace → fáze (astronomické pásy + denní oblouk).
 *
 *   < -18°          midnight (hluboká noc)
 *   -18 … -12       night / nautical (dle rising)
 *   -12 … -6        nautical dawn/dusk
 *   -6 … 0          civil dawn/dusk
 *   0 … 6           sunrise / sunset
 *   6 … 25          morning / golden|afternoon
 *   25 … 50         morning / afternoon
 *   > 50            noon (slunce vysoko)
 */
export function phaseFromElevation(elevation: number, rising: boolean): SolarPhase {
  if (elevation < -18) return 'midnight';
  if (elevation < -12) return rising ? 'night' : 'night';
  if (elevation < -6) return rising ? 'nautical_dawn' : 'nautical_dusk';
  if (elevation < 0) return rising ? 'civil_dawn' : 'civil_dusk';
  if (elevation < 6) return rising ? 'sunrise' : 'sunset';
  if (elevation < 12) return rising ? 'morning' : 'golden';
  if (elevation < 28) return rising ? 'morning' : 'afternoon';
  // Vysoko na obloze — poledne (i když ještě mírně stoupá / klesá)
  if (elevation >= 45) return 'noon';
  return rising ? 'morning' : 'afternoon';
}

export function getSolarSnapshot(
  date: Date = new Date(),
  lat: number = DEFAULT_SOLAR_COORDS.lat,
  lon: number = DEFAULT_SOLAR_COORDS.lon,
): SolarSnapshot {
  const { elevation, rising } = solarPosition(date, lat, lon);
  return {
    phase: phaseFromElevation(elevation, rising),
    elevation,
    rising,
    lat,
    lon,
    at: date,
  };
}

const COORDS_KEY = 'acm_solar_coords';

export function readStoredSolarCoords(): { lat: number; lon: number } {
  try {
    const raw = localStorage.getItem(COORDS_KEY);
    if (raw) {
      const p = JSON.parse(raw) as { lat?: number; lon?: number };
      if (typeof p.lat === 'number' && typeof p.lon === 'number') {
        return { lat: p.lat, lon: p.lon };
      }
    }
  } catch {
    /* ignore */
  }
  return { ...DEFAULT_SOLAR_COORDS };
}

export function storeSolarCoords(lat: number, lon: number): void {
  try {
    localStorage.setItem(COORDS_KEY, JSON.stringify({ lat, lon }));
  } catch {
    /* ignore */
  }
}

/** Geolokace, jinak uložené / Praha. */
export function resolveSolarCoords(): Promise<{ lat: number; lon: number }> {
  const fallback = readStoredSolarCoords();
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    return Promise.resolve(fallback);
  }
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lon = pos.coords.longitude;
        storeSolarCoords(lat, lon);
        resolve({ lat, lon });
      },
      () => resolve(fallback),
      { enableHighAccuracy: false, maximumAge: 86_400_000, timeout: 8_000 },
    );
  });
}

/** Jednorázový pokus o geolokaci (volitelné oprávnění). */
export function requestSolarGeolocation(): void {
  void resolveSolarCoords();
}
